import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'goober';

import { Button } from '@/ui/button/button';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { Stack } from '@/ui/line/line';
import { TextArea } from '@/ui/text-editable/text-editable.styles';

import { AskXFooterActions } from './ask-x-footer-actions';
import { useSendInquiry } from './use-send-inquiry.mutation';
import { showAskXResultState } from './asx-x.state';
import { colors } from '@/theme/colors';
import { PRODUCT } from '@/user/products/product.type';
import { useUserProducts } from '@/user/products/use-user-products';
import { Header } from '@/ui/typography/widgets';
import { useAtom } from 'jotai';

type AskXProps = {
  onClose?: () => void;
};

export const AskX = ({ onClose }: AskXProps) => {
  const { t } = useTranslation('askX');
  const [text, setText] = useState<string>('');
  const [showResultState, setShowResultState] = useAtom(showAskXResultState);

  const { isPending, mutateAsync } = useSendInquiry();

  const { hasRequiredProduct } = useUserProducts();
  if (!hasRequiredProduct(PRODUCT.ASK_X)) return null;

  const headerText = showResultState
    ? t`dropdown.resultTitle`
    : t`dropdown.title`;

  const sendInquiry = async () => {
    await mutateAsync(
      { message: text },
      {
        onSuccess: () => {
          setText('');
          setShowResultState(true);
        },
      },
    );
  };

  const handleChangeText = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ): void => {
    e.preventDefault();
    e.stopPropagation();
    setText(e.target.value);
  };

  return (
    <Stack fullWidth gap="14px">
      <StyledHeader color="white">{headerText}</StyledHeader>
      <Stack gap="26px" fullWidth>
        {showResultState ? (
          <Flexbox
            name="result-message"
            margin="0 0 64px 0"
          >{t`form.result`}</Flexbox>
        ) : (
          <>
            <Description>{t`form.description`}</Description>
            <StyledTextArea
              placeholder={t`form.placeholder`}
              onChange={handleChangeText}
              rows={6}
              resize="vertical"
              disabled={isPending}
              value={text}
            />
          </>
        )}
      </Stack>

      <AskXFooterActions
        actionComponent={
          !showResultState && (
            <Flexbox name="actions" gap="18px">
              <Button
                variant="underlined"
                color={colors.gray.c7}
                onClick={onClose}
                disabled={isPending}
              >{t`dropdown.cancel`}</Button>
              <Button
                variant="gray"
                disabled={text.length < 1}
                isLoading={isPending}
                onClick={sendInquiry}
              >{t`dropdown.send`}</Button>
            </Flexbox>
          )
        }
      />
    </Stack>
  );
};

const StyledHeader = styled(Header)`
  font-size: 20px;
`;

const StyledTextArea = styled(TextArea)`
  color: ${({ theme }) => theme.colors.basics.white};
  background-color: ${({ theme }) =>
    theme.palettes.component.askX.input.background};
  padding: 12px 16px 12px 16px;
  border-radius: 6px;
  border: ${({ theme }) => theme.palettes.component.askX.input.border} 2px solid;
  font-size: 12px;
`;

const Description = styled('p')`
  line-height: 20.44px;
`;
